import React, { FC } from 'react';

import { stripHtml } from '~/utils/strip-html';

import { StructuredDataPageDataValues, StructuredDataProps } from './models';
import { rawToBreadcrumbs } from './rawMappers/breadcrumbs';
import { rawToOrganization } from './rawMappers/organization';
import { articleMapper } from './structuredMappers/article';
import { bodyMapper } from './structuredMappers/body';
import { breadcrumbsMapper } from './structuredMappers/breadcrumbs';
import {
  organizationMapper,
  OrganizationMapperResult,
} from './structuredMappers/organization';
import { webPageMapper } from './structuredMappers/webPage';
import { useWebsiteData } from './useWebsiteData';
import { nonNullable } from './utils';

const generateStructuredData = ({
  data,
  organization,
  href,
}: {
  data?: StructuredDataPageDataValues;
  organization: OrganizationMapperResult;
  href: string;
}) => {
  const extraData: unknown[] = [];

  const dateModified = data?.updated_at || '';
  const datePublished = data?.publish_details?.time || '';

  if (data?.fields?.breadcrumbs) {
    extraData.push(
      breadcrumbsMapper(rawToBreadcrumbs(data.fields.breadcrumbs, organization))
    );
  }

  if (data && 'productData' in data.fields) {
    const { productTitle, descriptionShort, heroImage } =
      data.fields.productData;

    extraData.push(
      webPageMapper({
        name: productTitle ?? '',
        description: stripHtml(descriptionShort ?? ''),
        url: href,
        dateModified,
        datePublished,
        mainEntity: articleMapper({
          dateModified,
          datePublished,
          headline: productTitle ?? '',
          image: [heroImage.url].filter(Boolean),
          author: organization,
        }),
        publisher: organization,
      })
    );
  } else if (data && 'pageHeader' in data) {
    const { headingBlock, imageElement } = data.pageHeader;

    extraData.push(
      webPageMapper({
        name: headingBlock?.headline ?? '',
        description: stripHtml(headingBlock?.descriptionRte ?? ''),
        url: href,
        dateModified,
        datePublished,
        mainEntity: articleMapper({
          dateModified,
          datePublished,
          headline: headingBlock?.headline ?? '',
          image:
            imageElement
              ?.map((item) => {
                if ('url' in item.image) {
                  return item.image.url;
                }

                return null;
              })
              .filter(nonNullable) || [],
          author: organization,
        }),
        publisher: organization,
      })
    );
  }

  if (data && 'body' in data) {
    extraData.push(...bodyMapper(data.body, organization));
  }

  if (data && 'advices' in data) {
    extraData.push(
      ...bodyMapper(
        [
          {
            itemListing: { item: data.advices },
          },
        ],
        organization
      )
    );
  }

  return [organization, ...extraData];
};

export const Head: FC<StructuredDataProps> = ({
  data,
  pageContext: { locale },
}) => {
  const websiteData = useWebsiteData(locale);

  if (process.env.GATSBY_DISABLE_STRUCTURED_DATA === 'true') {
    console.warn(
      'Structured data/JSON-DL have been disabled, see documentation for more information'
    );

    return null;
  }

  if (!websiteData.settings) {
    console.error(
      `Missing settings for StructuredData with tag ${process.env.GATSBY_CONTENTSTACK_BRAND_TAG}`
    );

    return null;
  }

  const organization = organizationMapper(rawToOrganization(websiteData));

  const contentstackData =
    data?.contentstackL1ContentPage ??
    data?.contentstackL1Homepage ??
    data?.contentstackL2ContentPage ??
    data?.contentstackL2ProductPage ??
    data?.contentstackLdsPage ??
    data?.contentstackL2ProductPageWithSplitter;

  const structuredData = generateStructuredData({
    href: websiteData.href,
    data: contentstackData,
    organization,
  });

  return (
    <script type="application/ld+json">
      {JSON.stringify(structuredData, null, 2)}
    </script>
  );
};
